// Where a solo solve lives: `crosswordProgress/<uid>/<date>` in the hub
// database, mirrored in localStorage so a refresh mid-puzzle (or a puzzle
// started before sign-in settled) never loses a letter. The store debounces
// writes; this module only knows the shape.
//
//   { letters: { "r-c": "A" }, seconds, solvedAt, reveals: { "r-c": true },
//     updatedAt }
import { ref as dbRef, get, set } from 'firebase/database';
import { db } from '@/firebase';

export const PROGRESS_PATH = 'crosswordProgress';
const LOCAL_KEY = (date) => `crossword:progress:${date}`;

export function readLocal(date) {
  try {
    const raw = window.localStorage.getItem(LOCAL_KEY(date));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function writeLocal(date, progress) {
  try {
    window.localStorage.setItem(LOCAL_KEY(date), JSON.stringify(progress));
  } catch { /* storage off */ }
}

export async function readRemote(uid, date) {
  if (!db || !uid) return null;
  try {
    const snap = await get(dbRef(db, `${PROGRESS_PATH}/${uid}/${date}`));
    return snap.exists() ? snap.val() : null;
  } catch { return null; }
}

export async function writeRemote(uid, date, progress) {
  if (!db || !uid) return;
  try {
    await set(dbRef(db, `${PROGRESS_PATH}/${uid}/${date}`), progress);
  } catch (error) {
    console.error('crossword: progress save failed', error);
  }
}

/** The whole progress tree for the archive's "solved / in progress" marks. */
export async function readAllRemote(uid) {
  if (!db || !uid) return {};
  try {
    const snap = await get(dbRef(db, `${PROGRESS_PATH}/${uid}`));
    return snap.exists() ? snap.val() : {};
  } catch { return {}; }
}

/** Letters as stored ("r-c" -> "A") from the in-memory shape ("r-c" -> { l, by }). */
export const flattenLetters = (letters) => {
  const out = {};
  for (const [k, v] of Object.entries(letters)) {
    const l = typeof v === 'string' ? v : v?.l;
    if (l) out[k] = l;
  }
  return out;
};

/** The newer of two progress records wins, letter-count breaking ties. */
export function newer(a, b) {
  if (!a) return b;
  if (!b) return a;
  const la = Object.keys(a.letters || {}).length;
  const lb = Object.keys(b.letters || {}).length;
  if ((a.updatedAt || 0) === (b.updatedAt || 0)) return la >= lb ? a : b;
  return (a.updatedAt || 0) > (b.updatedAt || 0) ? a : b;
}
